import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Box,
  Typography,
  Chip,
  Autocomplete,
  CircularProgress,
  Grid,
  createFilterOptions,
} from "@mui/material";
import { formatAssigneeName } from "../../../../setup/helpers";
import { listCrmTags } from "../../api/actions/query";

const filterTagOptions = createFilterOptions();

const leadSourceOptions = [
  "Google Search",
  "LinkedIn",
  "Website",
  "Referral",
  "Manual",
  "Campaign",
  "Other",
];

const BulkUpdateDialog = ({ open, onClose, selectedCount, onBulkUpdate, isSubmitting, availableTags = [] }) => {
  const [tagOptions, setTagOptions] = useState(availableTags || []);

  useEffect(() => {
    if (availableTags && availableTags.length > 0) {
      setTagOptions((prev) => Array.from(new Set([...prev, ...availableTags])).sort());
    }
  }, [availableTags]);

  useEffect(() => {
    let isMounted = true;
    const fetchTags = async () => {
      try {
        const res = await listCrmTags();
        if (isMounted && res?.data?.success && Array.isArray(res.data.data)) {
          setTagOptions((prev) => {
            const combined = Array.from(new Set([...prev, ...res.data.data])).filter(Boolean);
            return combined.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
          });
        }
      } catch (err) {
        console.warn("BulkUpdate: could not fetch tags", err);
      }
    };
    if (open) {
      fetchTags();
    }
    return () => {
      isMounted = false;
    };
  }, [open]);

  const [fieldsToUpdate, setFieldsToUpdate] = useState({
    customerType: false,
    leadStatus: false,
    assignedTo: false,
    leadSource: false,
    tags: false,
  });

  const [values, setValues] = useState({
    customerType: "",
    leadStatus: "Contact",
    assignedTo: "",
    leadSource: "Manual",
    tags: [],
    tagAction: "add",
  });

  const handleToggleField = (field) => {
    setFieldsToUpdate((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleChangeValue = (field, val) => {
    setValues((prev) => ({ ...prev, [field]: val }));
  };

  const handleSave = () => {
    const updates = {};
    if (fieldsToUpdate.customerType) updates.customerType = values.customerType;
    if (fieldsToUpdate.leadStatus) updates.leadStatus = values.leadStatus;
    if (fieldsToUpdate.assignedTo) updates.assignedTo = formatAssigneeName(values.assignedTo);
    if (fieldsToUpdate.leadSource) updates.leadSource = values.leadSource;
    if (fieldsToUpdate.tags) updates.tags = values.tags;

    onBulkUpdate(updates, values.tagAction);
  };

  const hasSelectedFields = Object.values(fieldsToUpdate).some(Boolean);

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: "#0d3559", color: "#ffffff", py: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Bulk Update ({selectedCount} Contact{selectedCount > 1 ? "s" : ""})
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Select the fields you wish to update across all {selectedCount} selected contacts.
        </Typography>

        <Grid container spacing={2}>
          {/* Customer Type */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={fieldsToUpdate.customerType}
                  onChange={() => handleToggleField("customerType")}
                  size="small"
                />
              }
              label={<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Update Customer Type</Typography>}
            />
            {fieldsToUpdate.customerType && (
              <TextField
                fullWidth
                size="small"
                value={values.customerType}
                onChange={(e) => handleChangeValue("customerType", e.target.value)}
                placeholder="e.g. Candidate, Company, Customer"
                sx={{ mt: 1 }}
              />
            )}
          </Grid>

          {/* Lead Status */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={fieldsToUpdate.leadStatus}
                  onChange={() => handleToggleField("leadStatus")}
                  size="small"
                />
              }
              label={<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Update Lead Status</Typography>}
            />
            {fieldsToUpdate.leadStatus && (
              <TextField
                select
                fullWidth
                size="small"
                value={values.leadStatus}
                onChange={(e) => handleChangeValue("leadStatus", e.target.value)}
                sx={{ mt: 1 }}
              >
                <MenuItem value="Contact">Contact</MenuItem>
                <MenuItem value="Lead">Lead</MenuItem>
                <MenuItem value="Prospect">Prospect</MenuItem>
                <MenuItem value="Customer">Customer</MenuItem>
              </TextField>
            )}
          </Grid>

          {/* Assigned To */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={fieldsToUpdate.assignedTo}
                  onChange={() => handleToggleField("assignedTo")}
                  size="small"
                />
              }
              label={<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Update Assigned To</Typography>}
            />
            {fieldsToUpdate.assignedTo && (
              <TextField
                fullWidth
                size="small"
                value={values.assignedTo}
                onChange={(e) => handleChangeValue("assignedTo", e.target.value)}
                placeholder="Enter assignee name"
                sx={{ mt: 1 }}
              />
            )}
          </Grid>

          {/* Lead Source */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={fieldsToUpdate.leadSource}
                  onChange={() => handleToggleField("leadSource")}
                  size="small"
                />
              }
              label={<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Update Lead Source</Typography>}
            />
            {fieldsToUpdate.leadSource && (
              <TextField
                select
                fullWidth
                size="small"
                value={values.leadSource}
                onChange={(e) => handleChangeValue("leadSource", e.target.value)}
                sx={{ mt: 1 }}
              >
                {leadSourceOptions.map((source) => (
                  <MenuItem key={source} value={source}>
                    {source}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>

          {/* Tags */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={fieldsToUpdate.tags}
                  onChange={() => handleToggleField("tags")}
                  size="small"
                />
              }
              label={<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Update Tags</Typography>}
            />
            {fieldsToUpdate.tags && (
              <Box sx={{ mt: 1, pl: 1 }}>
                <FormControl component="fieldset" sx={{ mb: 1 }}>
                  <FormLabel component="legend" sx={{ fontSize: "12px", fontWeight: 600 }}>
                    Tag Action
                  </FormLabel>
                  <RadioGroup
                    row
                    value={values.tagAction}
                    onChange={(e) => handleChangeValue("tagAction", e.target.value)}
                  >
                    <FormControlLabel value="add" control={<Radio size="small" />} label="Add Tags" />
                    <FormControlLabel value="remove" control={<Radio size="small" />} label="Remove Tags" />
                    <FormControlLabel value="replace" control={<Radio size="small" />} label="Replace All Tags" />
                  </RadioGroup>
                </FormControl>

                <Autocomplete
                  multiple
                  freeSolo
                  forcePopupIcon
                  filterSelectedOptions
                  options={tagOptions}
                  value={values.tags}
                  onChange={(event, newValue) => {
                    const cleaned = Array.from(
                      new Set(
                        newValue
                          .flatMap((item) => {
                            if (typeof item === "string") return item.split(",");
                            if (item && item.inputValue) return item.inputValue.split(",");
                            if (item && item.title && !item.inputValue) return item.title.split(",");
                            return [];
                          })
                          .map((item) => String(item).trim())
                          .filter(Boolean)
                      )
                    );
                    handleChangeValue("tags", cleaned);
                    setTagOptions((prev) =>
                      Array.from(new Set([...prev, ...cleaned])).sort((a, b) =>
                        a.localeCompare(b, undefined, { sensitivity: "base" })
                      )
                    );
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filterTagOptions(options, params);
                    const { inputValue } = params;
                    const trimmed = (inputValue || "").trim();
                    const isExisting = options.some(
                      (option) => trimmed.toLowerCase() === (typeof option === "string" ? option.toLowerCase() : "")
                    );
                    if (trimmed !== "" && !isExisting) {
                      filtered.push({
                        inputValue: trimmed,
                        title: `Add "${trimmed}"`,
                      });
                    }
                    return filtered;
                  }}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    if (option && option.inputValue) return option.inputValue;
                    if (option && option.title) return option.title;
                    return "";
                  }}
                  renderOption={(props, option) => {
                    const isNew = typeof option === "object" && option?.inputValue;
                    return (
                      <li {...props} key={typeof option === "string" ? option : option.inputValue}>
                        {isNew ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#0284c7", fontWeight: 600 }}>
                            <span>+</span> {option.title}
                          </Box>
                        ) : (
                          option
                        )}
                      </li>
                    );
                  }}
                  renderTags={(val, getTagProps) =>
                    val.map((option, index) => (
                      <Chip
                        variant="outlined"
                        size="small"
                        label={option}
                        {...getTagProps({ index })}
                        key={index}
                        sx={{ bgcolor: "#e0f2fe", color: "#0369a1", borderColor: "#bae6fd", fontWeight: 600 }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder={values.tags?.length ? "" : "Select existing tags or type to add new"}
                    />
                  )}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, px: 3 }}>
        <Button onClick={onClose} disabled={isSubmitting} sx={{ textTransform: "none", color: "#64748b" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasSelectedFields || isSubmitting}
          sx={{
            bgcolor: "#0d3559",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { bgcolor: "#092a46" },
          }}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isSubmitting ? "Updating..." : `Update ${selectedCount} Contacts`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkUpdateDialog;
